import { SearchBar } from '../SearchBar';
import { RestaurantCard } from '../RestaurantCard';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import type { Restaurant, SortBy } from '@/types';
import { useLanguage } from '@/contexts/AppContext';
import { useEffect } from 'react';
import { logger } from '@/utils/logger';

interface HomeContentProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onLocationClick: () => void;
  sortBy: SortBy;
  onSortChange: (value: SortBy) => void;
  filteredRestaurants: Restaurant[];
  isLoading: boolean;
  onFavoriteToggle: (restaurantId: string) => void;
  onRestaurantSelect: (restaurantId: string) => void;
  hasNoNearbyStores?: boolean;
  apiError?: Error | null;
}

export function HomeContent({
  searchQuery,
  onSearchChange,
  onLocationClick,
  sortBy,
  onSortChange,
  filteredRestaurants,
  isLoading,
  onFavoriteToggle,
  onRestaurantSelect,
  hasNoNearbyStores = false,
  apiError = null,
}: HomeContentProps) {
  const { t } = useLanguage();

  // Props 검증 - 개발 환경에서만 실행
  useEffect(() => {
    if (import.meta.env.DEV) {
      // 필수 props 검증
      const requiredProps: (keyof HomeContentProps)[] = [
        'searchQuery',
        'onSearchChange',
        'onLocationClick',
        'sortBy',
        'onSortChange',
        'filteredRestaurants',
        'isLoading',
        'onFavoriteToggle',
        'onRestaurantSelect',
      ];

      const missingProps: string[] = [];

      for (const prop of requiredProps) {
        if (prop === 'searchQuery' && typeof searchQuery !== 'string') {
          missingProps.push(`${prop}: expected string, got ${typeof searchQuery}`);
        }
        if (prop === 'filteredRestaurants' && !Array.isArray(filteredRestaurants)) {
          missingProps.push(`${prop}: expected array, got ${typeof filteredRestaurants}`);
        }
        if (prop === 'isLoading' && typeof isLoading !== 'boolean') {
          missingProps.push(`${prop}: expected boolean, got ${typeof isLoading}`);
        }
      }

      // 함수 타입 props 검증
      if (typeof onSearchChange !== 'function') {
        missingProps.push('onSearchChange: expected function');
      }
      if (typeof onSortChange !== 'function') {
        missingProps.push('onSortChange: expected function');
      }
      if (typeof onFavoriteToggle !== 'function') {
        missingProps.push('onFavoriteToggle: expected function');
      }
      if (typeof onRestaurantSelect !== 'function') {
        missingProps.push('onRestaurantSelect: expected function');
      }
      if (typeof onLocationClick !== 'function') {
        missingProps.push('onLocationClick: expected function');
      }

      if (missingProps.length > 0) {
        logger.error('[HomeContent] Props validation failed:', {
          missingProps,
          props: {
            searchQuery,
            filteredRestaurants: Array.isArray(filteredRestaurants) ? `${filteredRestaurants.length} items` : 'not array',
            isLoading,
            sortBy,
          },
        });
      }
    }
  }, [
    searchQuery,
    onSearchChange,
    onLocationClick,
    sortBy,
    onSortChange,
    filteredRestaurants,
    isLoading,
    onFavoriteToggle,
    onRestaurantSelect,
  ]);
  return (
    <div className="flex-1 pb-[calc(5rem+2vw)] p-[0px] m-[0px]">
      {/* (#414) currentTable 활성 테이블 배너 제거 — currentTable 이 dead state(항상 null)라
          렌더된 적 없는 dead JSX. 활성 테이블 세션 표시는 GlobalTableSessionBar(#660)가 담당. */}
      <SearchBar
        placeholder={t.searchPlaceholder}
        value={searchQuery}
        onChange={onSearchChange}
        onLocationClick={onLocationClick}
      />

      <div className="p-[min(5vw,1.25rem)]">
        <div className="flex items-center justify-between m-[0px]">
          <h2 className="text-[16px] font-medium text-foreground">{t.chooseFavorites}</h2>
          <Select value={sortBy} onValueChange={(value: SortBy) => onSortChange(value)}>
            <SelectTrigger className="w-[140px] h-8 text-[calc(0.75rem+0.125vw)] bg-transparent border-border text-muted-foreground">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="distance">{t.sortBy.distance}</SelectItem>
              <SelectItem value="name">{t.sortBy.name}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <p className="text-[14px] text-muted-foreground mb-[min(3vw,1rem)]">
          {filteredRestaurants.length} {t.resultsCount}
        </p>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="ml-2 text-muted-foreground">{t.loading}</span>
          </div>
        ) : apiError ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 mb-4 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-foreground mb-2">
              {t.errorLoadingStores}
            </h3>
            <p className="text-sm text-muted-foreground max-w-xs">
              {apiError.message || t.errorLoadingStoresMessage}
            </p>
          </div>
        ) : hasNoNearbyStores ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 mb-4 rounded-full bg-muted flex items-center justify-center">
              <svg className="w-8 h-8 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-foreground mb-2">
              {t.noNearbyStores}
            </h3>
            <p className="text-sm text-muted-foreground max-w-xs">
              {t.noNearbyStoresMessage}
            </p>
          </div>
        ) : filteredRestaurants.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center" role="status">
            <div className="w-16 h-16 mb-4 rounded-full bg-muted flex items-center justify-center">
              <svg className="w-8 h-8 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-foreground mb-2">
              {t.noNearbyStores}
            </h3>
            <p className="text-sm text-muted-foreground max-w-xs">
              {t.noNearbyStoresMessage}
            </p>
          </div>
        ) : (
          <div className="space-y-[min(2vw,0.5rem)]">
            {filteredRestaurants.map((restaurant, index) => {
              // API 매장은 _name 사용, 그 외는 번역 사용
              const name = restaurant._name || t.restaurants[restaurant.nameKey]?.name || restaurant.nameKey;
              const cuisine = restaurant._cuisine || t.restaurants[restaurant.cuisineKey]?.cuisine || '';
              return (
                <div
                  key={restaurant.id}
                  className="transform transition-all duration-500 ease-out opacity-0 translate-y-4 animate-fadeInUp"
                  style={{
                    animationDelay: `${index * 100}ms`,
                    animationFillMode: 'forwards'
                  }}
                >
                  <div className="hover:scale-[1.02] transition-transform duration-200 hover:shadow-lg rounded-2xl">
                    <RestaurantCard
                      id={restaurant.id}
                      name={name}
                      cuisine={cuisine ? `${name} ${cuisine}` : name}
                      rating={restaurant.rating}
                      deliveryTime={restaurant.deliveryTime}
                      distance={restaurant.distance}
                      imageUrl={restaurant.imageUrl}
                      isFavorite={restaurant.isFavorite}
                      isOpen={restaurant.isOpen}
                      showRating={false}
                      showDeliveryTime={false}
                      onFavoriteToggle={() => onFavoriteToggle(restaurant.id)}
                      onClick={() => onRestaurantSelect(restaurant.id)}
                      pickupWaitlistBadge={
                        restaurant.pickup_requires_waitlist ? t.pickupWaitlistBadge : undefined
                      }
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
